import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { openCalendlyPopup } from '@/lib/calendly';
import { ArrowRight, Code2, Database, Cloud, Cpu, Shield, Zap, Globe, Layers } from 'lucide-react';
import { useEffect, useState, memo, useRef, Suspense, lazy } from 'react';
import goodFirmsFeaturedBadge from '@/assets/badges/goodfirms-featured-fde.png';
const heroBg = '/hero-bg.webp';

const DigitalBrain = lazy(() => import('@/components/3d/DigitalBrain'));

// Memoized floating icon component for performance
const FloatingIcon = memo(({ Icon, className, delay, size }: {
  Icon: typeof Code2;
  className: string;
  delay: string;
  size: number
}) => (
  <div
    className={`absolute hidden lg:flex items-center justify-center w-14 h-14 glass-float rounded-xl text-primary/60 ${className}`}
    style={{ animationDelay: delay }}
  >
    <Icon size={size} strokeWidth={1.5} />
  </div>
));
FloatingIcon.displayName = 'FloatingIcon';

const floatingIcons = [
  { Icon: Code2, className: 'top-[15%] left-[8%]', delay: '0s', size: 28 },
  { Icon: Database, className: 'top-[25%] right-[12%]', delay: '1.5s', size: 24 },
  { Icon: Cloud, className: 'top-[45%] left-[5%]', delay: '3s', size: 32 },
  { Icon: Cpu, className: 'bottom-[35%] right-[8%]', delay: '0.5s', size: 26 },
  { Icon: Shield, className: 'top-[12%] right-[25%]', delay: '2s', size: 22 },
  { Icon: Zap, className: 'bottom-[20%] left-[12%]', delay: '2.5s', size: 24 },
  { Icon: Globe, className: 'top-[60%] right-[18%]', delay: '1s', size: 28 },
  { Icon: Layers, className: 'bottom-[45%] left-[18%]', delay: '3.5s', size: 22 },
];

const stats = [
  { value: '13+', label: 'Years of Experience' },
  { value: '1,000+', label: 'Projects Delivered' },
  { value: '5/5', label: 'Rated on Clutch & GoodFirms' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '200+', label: 'Tech Experts' },
];


export function HeroSection() {
  const [isMobile, setIsMobile] = useState(false);
  const [showInteractiveBackground, setShowInteractiveBackground] = useState(false);
  const heroImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setShowInteractiveBackground(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (window.innerWidth < 768) return;

    const idleCallback = window.requestIdleCallback?.(() => {
      setShowInteractiveBackground(true);
    }, { timeout: 2500 });

    const timeoutId = window.setTimeout(() => {
      setShowInteractiveBackground(true);
    }, 1800);

    return () => {
      if (idleCallback) {
        window.cancelIdleCallback?.(idleCallback);
      }
      window.clearTimeout(timeoutId);
    };
  }, [isMobile]);

  useEffect(() => {
    let animationFrameId: number;

    const handleScroll = () => {
      // Disable parallax on mobile for performance
      if (window.innerWidth < 768 || !heroImageRef.current) return;

      // Only update if scroll is within hero section height for performance
      if (window.scrollY < window.innerHeight * 1.5) {
        animationFrameId = requestAnimationFrame(() => {
          if (heroImageRef.current) {
            const translateY = window.scrollY * 0.3;
            heroImageRef.current.style.transform = `translate3d(0, ${translateY}px, 0)`;
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden bg-background w-full">
      {/* Render a static hero image immediately, then upgrade to 3D on idle for desktop. */}
      <img
        ref={heroImageRef}
        src={heroBg}
        srcSet="/hero-bg-mobile.webp 768w, /hero-bg.webp 1920w"
        sizes="100vw"
        alt="Custom software development and AI solutions by Ortem Technologies"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${showInteractiveBackground ? 'opacity-0' : 'opacity-100'}`}
        loading="eager"
        decoding="sync"
        width={1920}
        height={1080}
        // React 18 DOM expects the lowercase `fetchpriority` attribute; the camelCase
        // prop triggers a dev warning. Spread the lowercase attr (cast to bypass TS types).
        {...({ fetchpriority: 'high' } as Record<string, string>)}
      />

      {!isMobile && showInteractiveBackground && (
        <Suspense fallback={<div className="absolute inset-0 bg-background" />}>
          <div className="absolute inset-0 animate-fade-in">
            <DigitalBrain />
          </div>
        </Suspense>
      )}
      <div className="absolute inset-0 bg-background/80 pointer-events-none" />

      {/* Floating Tech Icons - Desktop Only */}
      {!isMobile && floatingIcons.map((props, index) => (
        <FloatingIcon key={index} {...props} />
      ))}

      {/* "As Featured on GoodFirms" badge - Desktop Only */}
      {!isMobile && (
        <a
          href="https://www.goodfirms.co/resources/forward-deployed-engineering-over-it-staff-augmentation?utm_source=social&utm_medium=social&utm_campaign=fde_research_video"
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="absolute z-20 hidden lg:block bottom-[22%] left-[2%] w-44 xl:w-52 animate-float hover:scale-105 transition-transform duration-300 drop-shadow-xl"
          aria-label="Ortem Technologies featured on GoodFirms — Forward Deployed Engineering research"
        >
          <img src={goodFirmsFeaturedBadge} alt="Featured on GoodFirms" className="w-full h-auto rounded-2xl" />
        </a>
      )}

      {/* Floating Orbs - Hide on Mobile for Performance */}
      <div className="hidden md:block orb orb-primary w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] top-[-15%] right-[-10%]" />
      <div className="hidden md:block orb orb-accent w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] bottom-[-10%] left-[-10%] animate-float-delayed" />
      <div className="hidden md:block orb orb-light w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] top-[40%] left-[50%] animate-float-slow" />


      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full glass-card mb-6 sm:mb-8 animate-fade-in shimmer">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-xs sm:text-sm text-muted-foreground font-medium">
              Trusted by 300+ businesses worldwide — USA, UK, Australia, New Zealand, Middle East &amp; more
            </span>
          </div>

          {/* H1 — primary headline, largest element */}
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-4 animate-fade-in">
            <span className="gradient-text-animated">Enterprise-Grade</span>{' '}
            Custom Software &amp; AI Development
          </h1>

          {/* Slogan — supporting, visually subordinate */}
          <p className="font-display text-xl sm:text-2xl md:text-3xl font-semibold text-foreground/70 mb-3 animate-fade-in">
            Technology With Soul. Strategy With Fire.
          </p>

          {/* One-line value prop */}
          <p className="hero-description text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed animate-fade-in px-4" data-speakable>
            We build web platforms, mobile apps, and AI systems for healthcare, fleet &amp; logistics, and SaaS businesses — shipping production software since 2012 with 98% client retention.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 animate-fade-in px-4" style={{ animationDelay: '0.3s' }}>
            <Button
              variant="hero"
              size="xl"
              className="w-full sm:w-auto"
              onClick={() => openCalendlyPopup('https://calendly.com/pravinjha/30min')}
            >
              Book a Free Consultation
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <Button variant="hero-outline" size="xl" className="w-full sm:w-auto" asChild>
              <Link to="/case-studies/">
                See Our Case Studies
              </Link>
            </Button>
          </div>

          {/* Stats — Glass Cards */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            {stats.map((stat, index) => (
              <div
                key={index}
                className="glass-card-hover p-3 sm:p-5 rounded-xl sm:rounded-2xl text-center hover:scale-105 transition-transform duration-200"
              >
                <div className="font-display text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 pb-1">
                  <span className="gradient-text px-1 pb-1 inline-block">{stat.value}</span>
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Feature pills — trust signals below stats */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-6 animate-fade-in text-sm text-foreground/70 font-medium">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <span>AI-Driven</span>
            </div>
            <span className="text-border hidden sm:block">·</span>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span>Future-Proof</span>
            </div>
            <span className="text-border hidden sm:block">·</span>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              <span>Scalable Architecture</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
